import { useState } from 'react';
import { useAppStore, useProductStore, useReceiptStore } from 'store';
import { SCREEN_TYPES } from 'utils/constants';
import { Button } from 'components/ui/Button';
import { CardTitle } from 'components/ui/card';
import { handleProblemResolve } from 'utils/handleProblem';
import { handleScanItemPageOpened } from '../utils';
import { MoveToPRScreen } from './MoveToPRScreen';

export default function ConfirmItem() {
  const [loading, setLoading] = useState(false);
  const { setCurrentScreen } = useAppStore();
  const { currentProduct: productLine, setCurrentProduct } = useProductStore();
  const { receiptData } = useReceiptStore();

  const onConfirm = () => setCurrentScreen(SCREEN_TYPES.GRADE_ITEM);
  const isUnexpected = productLine?.product_id === 'Unknown' || productLine?.expected_ind === 'n';

  const handleProblem = async (): Promise<void> => {
    setLoading(true);
    const result = await handleProblemResolve(SCREEN_TYPES.CONFIRM_ITEM);
    if (!result.success) {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    handleScanItemPageOpened();
    setCurrentProduct(null);
  };

  if (isUnexpected) {
    return (
      <MoveToPRScreen
        trackingId={receiptData?.tracking_id || receiptData?.receipt?.tracking_id || ''}
        responseState={receiptData?.responseState || 'unknown'}
        responseMsg={receiptData?.responseMsg || 'No response message'}
        title="Confirm Item"
        handleCancel={handleCancel}
        children={
          <Button type="button" variant="outline" onClick={handleProblem} disabled={loading}>
            {loading ? 'Reporting...' : 'Problem'}
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <CardTitle className="mb-1 flex items-center justify-center gap-3 text-center text-xl font-extrabold tracking-tight text-slate-900">
        Confirm Item
      </CardTitle>

      <div className="space-y-1 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-1 border-b border-slate-100 py-1.5">
          <span className="text-sm font-semibold text-slate-600">Tracking #</span>
          <span className="break-all text-sm font-semibold text-slate-900">
            {receiptData?.tracking_id || receiptData?.receipt?.tracking_id}
          </span>
        </div>

        <div className="flex flex-col gap-1 border-b border-slate-100 py-1.5">
          <span className="text-sm font-semibold text-slate-600">Article #</span>
          <span className="break-all text-sm font-semibold text-slate-900">
            {isUnexpected ? 'Unknown' : productLine?.product_id}
          </span>
        </div>

        <div className="flex flex-col gap-1 border-b border-slate-100 py-1.5">
          <span className="text-sm font-semibold text-slate-600">Color:</span>
          <span className="break-all text-sm font-semibold text-slate-900">
            {isUnexpected ? 'N/A' : productLine?.style_color || 'N/A'}
          </span>
        </div>

        <div className="mb-1 flex flex-col gap-1 border-b border-slate-100 py-1.5">
          <span className="text-sm font-semibold text-slate-600">Size:</span>
          <span className="break-all text-sm font-semibold text-slate-900">
            {isUnexpected ? 'N/A' : productLine?.product_size || 'N/A'}
          </span>
        </div>
      </div>

      {productLine?.style_image_url && (
        <div className="flex min-h-[200px] items-center justify-center rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-3 text-lg font-semibold text-slate-400 shadow-sm">
          <img
            src={productLine.style_image_url}
            alt="Product"
            className="max-h-[300px] max-w-full object-contain"
          />
        </div>
      )}

      <div className="buttons-container">
        <Button
          type="button"
          variant="outline"
          onClick={handleScanItemPageOpened}
          disabled={loading}
          className="h-12 flex-1 rounded-xl border-2 border-red-600 text-base font-semibold text-red-600 hover:bg-red-50 hover:text-red-700"
        >
          Cancel
        </Button>

        <Button
          type="button"
          onClick={onConfirm}
          disabled={loading}
          className="h-12 flex-1 rounded-xl text-base font-semibold shadow-sm"
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
